// src/components/frontend/Registration.js
import React, { useState } from 'react'
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  FloatingLabel
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import indiaDistricts from '../../statesdistricts/indiaDistricts'
import {
  faUser,
  faEnvelope,
  faPhone,
  faBuilding,
  faBriefcase,
  faAddressCard,
  faUserGraduate,
  faGlobe,
  faPaperPlane,
  faCheckCircle,
  faSpinner,
  faIdCard,
  faCamera,
  faFileUpload,
  faUpload
} from '@fortawesome/free-solid-svg-icons'

import { memberService } from '../../services/api'

const Registration = () => {
  const [formData, setFormData] = useState({
    // Personal Information
    salutation: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',
    educationLevel: '',

    // Contact Information
    email: '',
    mobile: '',
    alternateMobile: '',
    address: '',
    city: '',
    state: '',
    district: '',
    pincode: '',

    // Professional Information
    profession: '',
    organization: '',
    designation: '',
    experience: '',
    expertise: '',
    mediaType: [],

    // Membership Details
    membershipType: '',
    howDidYouKnow: '',
    termsAccepted: false,

    // KYC Details
    aadharNumber: '',
    aadharFile: null,
    passportPhoto: null
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [fileErrors, setFileErrors] = useState({})

  const states = indiaDistricts.map(item => item.state)

  const districts = formData.state
    ? indiaDistricts.find(item => item.state === formData.state)?.districts ||
      []
    : []

  // File validation helper
  const validateFile = (
    file,
    maxSize = 5 * 1024 * 1024,
    allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
  ) => {
    if (!file) return { valid: false, error: 'File is required' }

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Invalid file type. Please upload JPEG, PNG, or PDF.'
      }
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit.`
      }
    }

    return { valid: true, error: '' }
  }

  const handleChange = e => {
    const { name, value, type, checked, files } = e.target

    if (type === 'file') {
      const file = files[0]

      // Handle file uploads
      if (name === 'aadharFile' || name === 'passportPhoto') {
        const validation = validateFile(file)
        if (!validation.valid) {
          setFileErrors({
            ...fileErrors,
            [name]: validation.error
          })
          return
        }

        // Clear error if valid
        setFileErrors({
          ...fileErrors,
          [name]: ''
        })

        setFormData({
          ...formData,
          [name]: file
        })
      }
    } else if (type === 'checkbox') {
      if (name === 'mediaType') {
        const newMediaType = [...formData.mediaType]

        if (checked) {
          newMediaType.push(value)
        } else {
          const index = newMediaType.indexOf(value)
          if (index > -1) {
            newMediaType.splice(index, 1)
          }
        }

        setFormData({
          ...formData,
          mediaType: newMediaType
        })
      } else {
        setFormData({
          ...formData,
          [name]: checked
        })
      }
    } else {
      // When state changes, clear the selected district
      if (name === 'state') {
        setFormData({
          ...formData,
          state: value,
          district: ''
        })
      } else {
        setFormData({
          ...formData,
          [name]: value
        })
      }
    }

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.membershipType.trim())
      newErrors.membershipType = 'Membership is required'

    // Personal Information Validation
    if (!formData.salutation.trim())
      newErrors.salutation = 'Salutation is required'
    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required'
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required'
    if (!formData.dateOfBirth)
      newErrors.dateOfBirth = 'Date of birth is required'
    if (!formData.gender) newErrors.gender = 'Please select your gender'
    if (!formData.nationality.trim())
      newErrors.nationality = 'Nationality is required'
    if (!formData.educationLevel.trim())
      newErrors.educationLevel = 'Education level is required'

    // Contact Validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required'
    } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number'
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required'
    if (!formData.city.trim()) newErrors.city = 'City is required'
    if (!formData.state.trim()) newErrors.state = 'State is required'
    if (!formData.district.trim()) newErrors.district = 'District is required'
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required'
    } else if (!/^[0-9]{5,6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter a valid pincode'
    }

    // KYC Validation
    if (!formData.aadharNumber) {
      newErrors.aadharNumber = 'Aadhar number is required'
    } else if (!/^[0-9]{12}$/.test(formData.aadharNumber)) {
      newErrors.aadharNumber = 'Please enter a valid 12-digit Aadhar number'
    }

    // Validate uploaded files
    if (!formData.aadharFile) {
      newErrors.aadharFile = 'Aadhar card upload is required'
    }

    if (!formData.passportPhoto) {
      newErrors.passportPhoto = 'Passport size photo is required'
    }

    // Terms Validation
    if (!formData.termsAccepted)
      newErrors.termsAccepted = 'You must accept the terms and conditions'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Add this function to your Registration component
 const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
        window.scrollTo(0, 0);
        return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
        // Create FormData for file upload
        const formDataToSend = new FormData();

        // Append all text fields (convert camelCase keys → snake_case for backend)
        const fieldMapping = {
            membershipType: 'membership_type',
            firstName: 'first_name',
            lastName: 'last_name',
            dateOfBirth: 'date_of_birth',
            educationLevel: 'education_level',
            alternateMobile: 'alternate_mobile',
            aadharNumber: 'aadhar_number',
            howDidYouKnow: 'how_did_you_know',
            termsAccepted: 'terms_accepted',
        };

        Object.keys(formData).forEach(key => {
            if (key === 'aadharFile' || key === 'passportPhoto') return;

            const backendKey = fieldMapping[key] || key;

            // Skip null/empty values (except termsAccepted)
            if (formData[key] === null || formData[key] === '') {
                if (key !== 'termsAccepted') return;
            }

            if (Array.isArray(formData[key])) {
                formData[key].forEach((item, index) => {
                    formDataToSend.append(`${backendKey}[${index}]`, item);
                });
                // If array is empty, still send it
                if (formData[key].length === 0) {
                    formDataToSend.append(backendKey, '');
                }
            } else if (typeof formData[key] === 'boolean') {
                formDataToSend.append(backendKey, formData[key] ? '1' : '0');
            } else {
                formDataToSend.append(backendKey, formData[key]);
            }
        });

        // Append files
        if (formData.aadharFile) {
            formDataToSend.append('aadhar_file', formData.aadharFile);
        }
        if (formData.passportPhoto) {
            formDataToSend.append('passport_photo', formData.passportPhoto);
        }

        // Make API call
        const response = await memberService.create(formDataToSend);

        // axios returns response.data — no need for response.json()
        if (response.data.success) {
            setSubmitSuccess(true);
            setIsSubmitting(false);
        } else {
            throw new Error(response.data.message || 'Registration failed');
        }

    } catch (error) {
        console.error('Registration error:', error);

        if (error.response) {
            const { status, data } = error.response;

            // Handle 422 validation errors
            if (status === 422 && data.errors) {
                const fieldMap = {
                    membership_type: 'membershipType',
                    first_name: 'firstName',
                    last_name: 'lastName',
                    date_of_birth: 'dateOfBirth',
                    education_level: 'educationLevel',
                    alternate_mobile: 'alternateMobile',
                    aadhar_number: 'aadharNumber',
                    aadhar_file: 'aadharFile',
                    passport_photo: 'passportPhoto',
                    how_did_you_know: 'howDidYouKnow',
                    terms_accepted: 'termsAccepted',
                };

                const newErrors = {};
                Object.keys(data.errors).forEach(key => {
                    const fieldName = fieldMap[key] || key;
                    newErrors[fieldName] = Array.isArray(data.errors[key])
                        ? data.errors[key][0]
                        : data.errors[key];
                });

                setErrors(newErrors);
                setIsSubmitting(false);
                window.scrollTo(0, 0);
                return;
            }

            setErrors({
                general: data.message || `Server error (${status})`
            });
        } else if (error.request) {
            setErrors({
                general: 'No response from server. Please check your connection.'
            });
        } else {
            setErrors({
                general: error.message || 'Registration failed. Please try again.'
            });
        }

        setIsSubmitting(false);
        window.scrollTo(0, 0);
    }
};


if (submitSuccess) {
    return (
        <Container className='py-5'>
            <Row className='justify-content-center'>
                <Col md={8}>
                    <Card className='text-center shadow-lg border-0 p-5'>
                        <div className='mb-4'>
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                size='4x'
                                className='text-success'
                            />
                        </div>
                        <h2 className='fw-bold mb-3'>Registration Successful!</h2>
                        <p className='lead text-muted fw-bold'>
                            Thank you for registering with Indian Newspaper & Media Association.
                        </p>
                        <p className='text-muted'>
                            We have sent a confirmation email to{' '}
                            <strong>{formData.email}</strong>. Please check your email
                            for further instructions.
                        </p>
                        <div className='mt-3'>
                            <Button variant='primary' href='/' className='rounded-pill px-4'>
                                Go to Home
                            </Button>
                        </div>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

  // Custom styles for inputs
  const inputStyle = {
    border: '2px solid #1868b8',
    borderRadius: '8px',
    padding: '10px 14px',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
  }

  const selectStyle = {
    border: '2px solid #1868b8',
    borderRadius: '8px',
    padding: '10px 14px',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
  }

  const checkboxInputStyle = {
    width: '20px',
    height: '20px',
    border: '2px solid #1868b8',
    borderRadius: '4px',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
    accentColor: '#1868b8',
    marginTop: '2px'
  }

  // File input custom styling
  const fileInputStyle = {
    border: '2px dashed #1868b8',
    borderRadius: '8px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  }

  return (
    <section className='py-5' style={{ backgroundColor: '#e3f2fd' }}>
      <Container>
        <Row className='mb-5'>
          <Col className='text-center'>
            <h1 className='display-4 fw-bold'>Member Registration</h1>
            <div
              className='divider mx-auto'
              style={{
                width: '60px',
                height: '4px',
                background: 'linear-gradient(90deg, #0056b3, #007bff)',
                marginBottom: '1rem'
              }}
            />
            <p className='lead text-muted fw-medium'>
              Join the Indian Newspaper & Media Association and become part of a
              vibrant community of media professionals.
            </p>
          </Col>
        </Row>

        {errors.general && (
          <Alert variant='danger' className='mb-4'>
            {errors.general}
          </Alert>
        )}

        <Row className='justify-content-center'>
          <Col lg={10}>
            <Card className='shadow-lg border-0'>
              <Card.Body className='p-4 p-md-5'>
                <Form onSubmit={handleSubmit}>
                  <h4 className='fw-bold mb-4 pb-2 border-bottom'>
                    <FontAwesomeIcon
                      icon={faAddressCard}
                      className='text-primary me-2'
                    />
                    Membership Details
                  </h4>

                  <Row>
                    <Col md={6} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Membership Type
                      </Form.Label>
                      <Form.Select
                        name='membershipType'
                        value={formData.membershipType}
                        onChange={handleChange}
                        isInvalid={!!errors.membershipType}
                        style={selectStyle}
                        className='shadow-sm'
                      >
                        <option value=''>Select</option>
                        <option value='Professional Media Member'>
                          Professional Media Member
                        </option>
                        <option value='Student Media Member'>
                          Student Media Member
                        </option>
                        <option value='Corporate Member'>
                          Corporate Member
                        </option>
                        <option value='Business Member'>Business Member</option>
                        <option value='General Member'>General Member</option>
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>
                        {errors.membershipType}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Personal Information Section */}
                  <h4 className='fw-bold mt-5 mb-4 pb-2 border-bottom'>
                    <FontAwesomeIcon
                      icon={faUser}
                      className='text-primary me-2'
                    />
                    Personal Information
                  </h4>

                  <Row>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Salutation
                      </Form.Label>
                      <Form.Select
                        name='salutation'
                        value={formData.salutation}
                        onChange={handleChange}
                        style={selectStyle}
                        isInvalid={!!errors.salutation}
                        className='shadow-sm'
                      >
                        <option value=''>Select</option>
                        <option value='Mr.'>Mr.</option>
                        <option value='Ms.'>Ms.</option>
                        <option value='Mrs.'>Mrs.</option>
                        <option value='Dr.'>Dr.</option>
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>
                        {errors.salutation}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        First Name *
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='firstName'
                        placeholder='Enter first name'
                        value={formData.firstName}
                        onChange={handleChange}
                        isInvalid={!!errors.firstName}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.firstName}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Last Name *
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='lastName'
                        placeholder='Enter last name'
                        value={formData.lastName}
                        onChange={handleChange}
                        isInvalid={!!errors.lastName}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.lastName}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Date of Birth *
                      </Form.Label>
                      <Form.Control
                        type='date'
                        name='dateOfBirth'
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        isInvalid={!!errors.dateOfBirth}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.dateOfBirth}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>Gender *</Form.Label>
                      <Form.Select
                        name='gender'
                        value={formData.gender}
                        onChange={handleChange}
                        isInvalid={!!errors.gender}
                        style={selectStyle}
                        className='shadow-sm'
                      >
                        <option value=''>Select</option>
                        <option value='male'>Male</option>
                        <option value='female'>Female</option>
                        <option value='other'>Other</option>
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>
                        {errors.gender}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Nationality *
                      </Form.Label>

                      <Form.Select
                        name='nationality'
                        value={formData.nationality}
                        onChange={handleChange}
                        style={inputStyle}
                        isInvalid={!!errors.nationality}
                        className='shadow-sm'
                      >
                        <option value=''>Select nationality</option>
                        <option value='India'>India</option>
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>
                        {errors.nationality}
                      </Form.Control.Feedback>
                    </Col>

                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Education Level *
                      </Form.Label>

                      <Form.Select
                        name='educationLevel'
                        value={formData.educationLevel}
                        onChange={handleChange}
                        isInvalid={!!errors.educationLevel}
                        style={selectStyle}
                        className='shadow-sm'
                      >
                        <option value=''>Select Education Level</option>
                        <option value='below_10th'>Below 10th</option>
                        <option value='10th'>10th / SSLC</option>
                        <option value='12th'>12th / PUC</option>
                        <option value='iti'>ITI</option>
                        <option value='diploma'>Diploma</option>
                        <option value='graduate'>Graduate</option>
                        <option value='postgraduate'>Postgraduate</option>
                        <option value='phd'>Ph.D.</option>
                        <option value='other'>Other</option>
                      </Form.Select>

                      <Form.Control.Feedback type='invalid'>
                        {errors.educationLevel}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Contact Information Section */}
                  <h4 className='fw-bold mt-5 mb-4 pb-2 border-bottom'>
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className='text-primary me-2'
                    />
                    Contact Information
                  </h4>

                  <Row>
                    <Col md={6} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Email Address *
                      </Form.Label>
                      <Form.Control
                        type='email'
                        name='email'
                        placeholder='Enter email address'
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.email}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={6} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Mobile Number *
                      </Form.Label>
                      <Form.Control
                        type='tel'
                        name='mobile'
                        placeholder='Enter 10-digit mobile number'
                        value={formData.mobile}
                        onChange={handleChange}
                        isInvalid={!!errors.mobile}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.mobile}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Alternate Mobile
                      </Form.Label>
                      <Form.Control
                        type='tel'
                        name='alternateMobile'
                        placeholder='Enter alternate mobile number'
                        value={formData.alternateMobile}
                        onChange={handleChange}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                    </Col>
                    <Col md={6} className='mb-4'>
                      <Form.Label className='fw-semibold'>Address *</Form.Label>
                      <Form.Control
                        type='text'
                        name='address'
                        placeholder='Enter street address'
                        value={formData.address}
                        onChange={handleChange}
                        isInvalid={!!errors.address}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.address}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>State *</Form.Label>
                      <Form.Select
                        name='state'
                        value={formData.state}
                        onChange={handleChange}
                        isInvalid={!!errors.state}
                        style={selectStyle}
                        className='shadow-sm'
                      >
                        <option value=''>Select State</option>
                        {states.map(state => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>
                        {errors.state}
                      </Form.Control.Feedback>
                    </Col>

                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        District *
                      </Form.Label>
                      <Form.Select
                        name='district'
                        value={formData.district}
                        onChange={handleChange}
                        disabled={!formData.state}
                        style={selectStyle}
                        isInvalid={!!errors.district}
                        className='shadow-sm'
                      >
                        <option value=''>
                          {formData.state
                            ? 'Select District'
                            : 'Select State First'}
                        </option>
                        {districts.map(district => (
                          <option key={district} value={district}>
                            {district}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type='invalid'>
                        {errors.district}
                      </Form.Control.Feedback>
                    </Col>

                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        City / Village / Area *
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='city'
                        placeholder='Enter City / Village / Area'
                        value={formData.city}
                        onChange={handleChange}
                        isInvalid={!!errors.city}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.city}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>Pincode *</Form.Label>
                      <Form.Control
                        type='text'
                        name='pincode'
                        placeholder='Enter pincode'
                        value={formData.pincode}
                        onChange={handleChange}
                        isInvalid={!!errors.pincode}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.pincode}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Professional Information Section */}
                  <h4 className='fw-bold mt-5 mb-4 pb-2 border-bottom'>
                    <FontAwesomeIcon
                      icon={faBriefcase}
                      className='text-primary me-2'
                    />
                    Professional Information
                  </h4>

                  <Row>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Profession
                      </Form.Label>
                      <Form.Select
                        name='profession'
                        value={formData.profession}
                        onChange={handleChange}
                        style={selectStyle}
                        className='shadow-sm'
                      >
                        <option value=''>Select your profession</option>
                        <option value='journalist'>Journalist</option>
                        <option value='editor'>Editor</option>
                        <option value='reporter'>Reporter</option>
                        <option value='photographer'>Photographer</option>
                        <option value='videographer'>Videographer</option>
                        <option value='writer'>Writer/Author</option>
                        <option value='poet'>Poet</option>
                        <option value='artist'>Artist</option>
                        <option value='theater'>Theater Artist</option>
                        <option value='media_educator'>Media Educator</option>
                        <option value='student'>Media Student</option>
                        <option value='other'>Other</option>
                      </Form.Select>
                    </Col>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Organization
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='organization'
                        placeholder='Enter organization name'
                        value={formData.organization}
                        onChange={handleChange}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                    </Col>
                    <Col md={4} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Designation
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='designation'
                        placeholder='Enter your designation'
                        value={formData.designation}
                        onChange={handleChange}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Years of Experience
                      </Form.Label>
                      <Form.Select
                        name='experience'
                        value={formData.experience}
                        onChange={handleChange}
                        style={selectStyle}
                        className='shadow-sm'
                      >
                        <option value=''>Select</option>
                        <option value='0-1'>Less than 1 year</option>
                        <option value='1-3'>1-3 years</option>
                        <option value='3-5'>3-5 years</option>
                        <option value='5-10'>5-10 years</option>
                        <option value='10-15'>10-15 years</option>
                        <option value='15-20'>15-20 years</option>
                        <option value='20+'>20+ years</option>
                      </Form.Select>
                    </Col>
                    <Col md={6} className='mb-4'>
                      <Form.Label className='fw-semibold'>
                        Area of Expertise
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='expertise'
                        placeholder='e.g., Political Journalism, Sports, Entertainment'
                        value={formData.expertise}
                        onChange={handleChange}
                        style={inputStyle}
                        className='shadow-sm'
                      />
                    </Col>
                  </Row>

                  {/* KYC Details Section */}
                  <h4 className='fw-bold mt-5 mb-4 pb-2 border-bottom'>
                    <FontAwesomeIcon
                      icon={faIdCard}
                      className='text-primary me-2'
                    />
                    KYC Details
                  </h4>

                  {/* Aadhar Card Section */}
                  <Row className='mb-4'>
                    <Col md={12}>
                      <Card className='border border-primary bg-light'>
                        <Card.Body>
                          <h5 className='fw-bold mb-3'>
                            <FontAwesomeIcon
                              icon={faIdCard}
                              className='text-primary me-2'
                            />
                            Aadhar Card Details
                            <span className='text-danger ms-2'>*</span>
                          </h5>
                          <Row>
                            <Col md={6} className='mb-3'>
                              <Form.Label className='fw-semibold'>
                                Aadhar Number *
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='aadharNumber'
                                placeholder='Enter 12-digit Aadhar number'
                                value={formData.aadharNumber}
                                onChange={handleChange}
                                isInvalid={!!errors.aadharNumber}
                                style={inputStyle}
                                className='shadow-sm'
                                maxLength={12}
                              />
                              <Form.Control.Feedback type='invalid'>
                                {errors.aadharNumber}
                              </Form.Control.Feedback>
                            </Col>
                            <Col md={6} className='mb-3'>
                              <Form.Label className='fw-semibold'>
                                Upload Aadhar Card *
                              </Form.Label>
                              <Form.Control
                                type='file'
                                name='aadharFile'
                                onChange={handleChange}
                                accept='.jpg,.jpeg,.png,.pdf'
                                isInvalid={
                                  !!errors.aadharFile || !!fileErrors.aadharFile
                                }
                                style={fileInputStyle}
                                className='shadow-sm'
                              />
                              <Form.Text className='text-muted'>
                                Upload JPEG, PNG, or PDF (Max: 5MB)
                              </Form.Text>
                              {formData.aadharFile && (
                                <div className='mt-2 text-success'>
                                  <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className='me-1'
                                  />
                                  File selected: {formData.aadharFile.name}
                                </div>
                              )}
                              {(errors.aadharFile || fileErrors.aadharFile) && (
                                <div className='text-danger mt-2'>
                                  {errors.aadharFile || fileErrors.aadharFile}
                                </div>
                              )}
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Passport Size Photo Section */}
                  <Row className='mb-4'>
                    <Col md={12}>
                      <Card className='border border-success bg-light'>
                        <Card.Body>
                          <h5 className='fw-bold mb-3'>
                            <FontAwesomeIcon
                              icon={faCamera}
                              className='text-success me-2'
                            />
                            Passport Size Photo
                            <span className='text-danger ms-2'>*</span>
                          </h5>
                          <Row>
                            <Col md={12} className='mb-3'>
                              <Form.Label className='fw-semibold'>
                                Upload Passport Size Photo *
                              </Form.Label>
                              <Form.Control
                                type='file'
                                name='passportPhoto'
                                onChange={handleChange}
                                accept='.jpg,.jpeg,.png'
                                isInvalid={
                                  !!errors.passportPhoto ||
                                  !!fileErrors.passportPhoto
                                }
                                style={fileInputStyle}
                                className='shadow-sm'
                              />
                              <Form.Text className='text-muted'>
                                Upload JPEG or PNG (Max: 5MB). Recommended size:
                                2x2 inches.
                              </Form.Text>
                              {formData.passportPhoto && (
                                <div className='mt-2 text-success'>
                                  <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className='me-1'
                                  />
                                  File selected: {formData.passportPhoto.name}
                                </div>
                              )}
                              {(errors.passportPhoto ||
                                fileErrors.passportPhoto) && (
                                <div className='text-danger mt-2'>
                                  {errors.passportPhoto ||
                                    fileErrors.passportPhoto}
                                </div>
                              )}
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Terms and Conditions */}
                  <Row className='mt-5'>
                    <Col md={12}>
                      <div
                        className={`p-3 rounded ${
                          errors.termsAccepted
                            ? 'border border-danger'
                            : 'border border-primary'
                        }`}
                        style={{
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <Form.Check
                          type='checkbox'
                          id='termsAccepted'
                          name='termsAccepted'
                          checked={formData.termsAccepted}
                          onChange={handleChange}
                          isInvalid={!!errors.termsAccepted}
                          className='d-flex align-items-start'
                        >
                          <Form.Check.Input
                            type='checkbox'
                            name='termsAccepted'
                            checked={formData.termsAccepted}
                            onChange={handleChange}
                            isInvalid={!!errors.termsAccepted}
                            style={checkboxInputStyle}
                            className='me-2 mt-1'
                          />

                          <Form.Check.Label
                            htmlFor='termsAccepted'
                            style={{
                              cursor: 'pointer',
                              marginBottom: '0',
                              lineHeight: '1.6'
                            }}
                          >
                            I agree to the{' '}
                            <a href='/terms' target='_blank' rel='noreferrer'>
                              Terms and Conditions
                            </a>{' '}
                            and{' '}
                            <a href='/privacy' target='_blank' rel='noreferrer'>
                              Privacy Policy
                            </a>{' '}
                            of Indian Newspaper & Media Association. *
                          </Form.Check.Label>
                        </Form.Check>

                        {errors.termsAccepted && (
                          <div className='text-danger mt-2'>
                            {errors.termsAccepted}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Submit Button */}
                  <Row className='mt-5 pt-2'>
                    <Col className='text-center'>
                      <Button
                        type='submit'
                        variant='primary'
                        size='lg'
                        disabled={isSubmitting}
                        className='rounded-pill px-5 py-2'
                      >
                        {isSubmitting ? (
                          <>
                            <FontAwesomeIcon
                              icon={faSpinner}
                              spin
                              className='me-2'
                            />
                            Registering...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faPaperPlane}
                              className='me-2'
                            />
                            Register Now
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Benefits Section */}
        <Row className='mt-5'>
          <Col>
            <Card className='bg-primary text-white border-0'>
              <Card.Body className='p-4 text-center'>
                <h4 className='fw-bold mb-3'>Benefits of Membership</h4>
                <Row>
                  <Col md={3} className='mb-2 mb-md-0'>
                    <FontAwesomeIcon
                      icon={faUserGraduate}
                      size='2x'
                      className='mb-2'
                    />
                    <p className='mb-0'>Professional Development</p>
                  </Col>
                  <Col md={3} className='mb-2 mb-md-0'>
                    <FontAwesomeIcon
                      icon={faGlobe}
                      size='2x'
                      className='mb-2'
                    />
                    <p className='mb-0'>Networking Opportunities</p>
                  </Col>
                  <Col md={3} className='mb-2 mb-md-0'>
                    <FontAwesomeIcon
                      icon={faBuilding}
                      size='2x'
                      className='mb-2'
                    />
                    <p className='mb-0'>Industry Recognition</p>
                  </Col>
                  <Col md={3}>
                    <FontAwesomeIcon
                      icon={faBriefcase}
                      size='2x'
                      className='mb-2'
                    />
                    <p className='mb-0'>Career Growth</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default Registration
